import { useEffect, useCallback, useMemo, useRef } from 'react'

type UseBroadcastMessageProps = {
  channel: string
  message: string
  onMessage: () => void
}

export const useBroadcastMessage = ({
  channel,
  message,
  onMessage,
}: UseBroadcastMessageProps) => {
  const channelRef = useRef<BroadcastChannel | null>(null)

  const initializeChannel = useCallback(() => {
    if (!channelRef.current) {
      channelRef.current = new BroadcastChannel(channel)
    }
    return channelRef.current
  }, [channel])

  const broadcast = useCallback(() => {
    const channel = initializeChannel()
    channel.postMessage(message)
  }, [initializeChannel, message])

  useEffect(() => {
    const channel = initializeChannel()

    channel.onmessage = (event) => {
      if (event.data === message) {
        onMessage()
      }
    }

    return () => {
      if (channelRef.current) {
        channelRef.current.close()
        channelRef.current = null
      }
    }
  }, [channel, message, onMessage, initializeChannel])

  return useMemo(() => ({ broadcast }), [broadcast])
}
